import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// import { Idle } from '@ng-idle/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription, filter, fromEvent, skip } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CommonService } from './shared/services/common.service';
import { HeightService } from './shared/services/height.service';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ErrorLoggerService } from './core/services/error-logs.service';
import { AuthService } from './shared/services/auth.service';
import { SocketService, UserDeletedEventPayload } from './shared/services/socket.service';
import { UserStateService } from './shared/services/user-state.service';
import { CookieUtils } from './shared/Helper/cookie-utils';
import { registerPageUrl, trackPageview } from './shared/utils/mixpanel.util';

import { ToastrService } from 'ngx-toastr';
import { NetworkStatusService } from './core/services/network-status.service';
import { CommunityMembershipService } from './core/services/community-membership.service';
import { AppScrollService } from './shared/services/app-scroll.service';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    @ViewChild('announcementContainer', { static: false }) announcementContainer: ElementRef | undefined;
    dynamicHeight: number = 0;
    @ViewChild('sessionExpiryModal', { static: false }) sessionExpiryModal?: ModalDirective;
    public isOnSignInPage: boolean = true;
    public env = environment;
    public isGridView!: boolean;
    public showoverlay = false;
    idleWarningTime = 0;
    confirmationData = '';
    private headerHeightSubscription?: Subscription;
    private postViewHeaderHeightSubscription?: Subscription;
    private gridViewSubscription?: Subscription;
    private signInPageSubscription?: Subscription;
    private resizeObserver: ResizeObserver | null = null;
    private darkModeMediaQuery?: MediaQueryList;
    private darkModeChangeHandler?: () => void;
    private currentUserId: string | null = null;
    private readonly windowResizeHandler = () => {
        this.updateElementTop(this.heightService.getHeaderHeight());
    };
    private readonly unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
        this.errorLogger.handleError(event.reason ?? new Error('Unhandled promise rejection'));
    };
    showMenu = false;

    constructor(
        private commonservices: CommonService,
        private cdref: ChangeDetectorRef,
        // private idle: Idle,
        private renderer: Renderer2,
        private heightService: HeightService,
        private ngZone: NgZone,
        private activatedRoute: ActivatedRoute,
        private commonService: CommonService,
        private router: Router,
        private appScroll: AppScrollService,
        private errorLogger: ErrorLoggerService,
        private readonly socketService: SocketService,
        private readonly userState: UserStateService,
        private readonly appAuthService: AuthService,
        private readonly networkStatus: NetworkStatusService,
        private readonly toastr: ToastrService,
        private readonly communityMembership: CommunityMembershipService
    ) {
        // this.idle.onIdleStart.subscribe(() => {
        //     this.sessionExpiryModal?.show();
        // });
    }

    ngOnInit(): void {
        // captureAdParams() + registerAcquisitionProps() moved to main.ts, ahead of
        // mixpanelInit(). Running them here (post-bootstrap) meant the session's first
        // $mp_web_page_view had already been sent without first_*/environment.
        const isProd = environment.production;

        if (!isProd) {
            const meta = document.createElement('meta');
            meta.name = 'robots';
            meta.content = 'noindex, nofollow';
            document.head.appendChild(meta);
        }

        const viewFromUrl = this.activatedRoute.snapshot.queryParamMap.get('view');
        if (viewFromUrl !== null) {
            this.isGridView = viewFromUrl === 'grid';
            localStorage.setItem('gridView', String(this.isGridView));
        } else {
            const storedView = localStorage.getItem('gridView');
            this.isGridView = storedView !== null ? storedView === 'true' : true;
        }
        this.commonService.setView(this.isGridView);
        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => this.appScroll.navigationStarted(event));
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => {
                const urlTree = this.router.parseUrl(this.router.url);
                const referralKey = urlTree.queryParams['ref'];
                if (referralKey) {
                    CookieUtils.setCookie('referral_key', referralKey, 90);
                }
                // Keep the query-free page_url super-prop in step with the route.
                // $current_url is blacklisted (it leaks reset/verify tokens), so this
                // is the only page context non-pageview events get — stale here means
                // every event reports whichever page the session started on.
                // Router scroll restoration only ever scrolled the document, which no longer
                // moves. Position the shell's container instead: Back/Forward returns to where
                // that page was left, every other route opens at the top.
                this.appScroll.navigationEnded(event);
                registerPageUrl();
                // Then the pageview for the route we just landed on. This is now the ONLY
                // source of pageviews: autocapture's built-in pageview tracking is
                // disabled (it fired a duplicate of ours on load AND owned every in-app
                // route pageview via its own locationchange listener). Order matters —
                // registerPageUrl() first, so the event carries the page it belongs to.
                // Angular emits NavigationEnd for the initial navigation too, which is
                // why mixpanelInit() deliberately does NOT fire one at bootstrap.
                trackPageview();
            });
        this.darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const manifestElement = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;

        const updateManifest = () => {
            if (!manifestElement?.href) {
                return;
            }

            fetch(manifestElement.href)
                .then(response => response.json())
                .then(manifest => {
                    if (this.darkModeMediaQuery?.matches) {
                        manifest.icons = manifest.icons_dark;
                        manifest.theme_color = '#000000';
                    } else {
                        manifest.icons = manifest.icons_light;
                        manifest.theme_color = '#ffffff';
                    }

                    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
                    manifestElement.href = URL.createObjectURL(blob);
                })
                .catch((error) => this.errorLogger.handleError(error));
        };

        updateManifest();
        this.darkModeChangeHandler = () => updateManifest();
        this.darkModeMediaQuery.addEventListener('change', this.darkModeChangeHandler);

        this.headerHeightSubscription = this.heightService.headerHeight$.subscribe((headerHeight) => {
            this.updateElementTop(headerHeight);
        });
        this.setupResizeObserver();
        window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);

        // Log visibility is controlled by LoggerService and environment log levels.
        // Disabling console output is not treated as a security control.
        this.gridViewSubscription = this.commonservices.showGridViewObservable.subscribe((response: boolean) => {
            this.isGridView = response;
        });
        this.signInPageSubscription = this.commonservices.siginpageObservable.subscribe((response: boolean) => {
            this.isOnSignInPage = response;
            // The header is added or removed with this flag; re-measure once that has
            // rendered so the scroll container's top offset follows it.
            setTimeout(() => this.updateElementTop(this.heightService.getHeaderHeight()));
        });
        this.initializeGlobalAuthListeners();
        if (this.env.production) {
            this.loadGoogleAnalytics();
        }
    }

    ngAfterContentChecked(): void {
        this.cdref.detectChanges();
    }

    loadGoogleAnalytics() {
        const gtmId = this.env.GOOGLE_ANALYTICS_KEY;
        const dataLayerName = 'dataLayer';

        if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
            return;
        }

        const windowWithDataLayer = window as unknown as Window & { [key: string]: unknown[] } & { gtag?: (...args: unknown[]) => void };
        const existingLayer = windowWithDataLayer[dataLayerName];
        const dataLayer = Array.isArray(existingLayer) ? existingLayer : [];
        windowWithDataLayer[dataLayerName] = dataLayer;
        dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' } as never);

        // gtm.js does NOT create a global gtag(); only gtag.js does. The container holds a
        // Custom HTML tag that calls gtag('event', 'conversion_event_purchase', {}), so
        // without this shim it threw "gtag is not defined" from inside gtm.js on every page.
        // Same one-liner Google's own gtag.js snippet installs: queue the call on dataLayer
        // and let the container's Google tag consume it.
        // Pushes the raw `arguments` object, not a rest array: GTM reads gtag commands as
        // array-like entries, so keeping Google's exact shape is what makes them parse.
        if (typeof windowWithDataLayer.gtag !== 'function') {
            windowWithDataLayer.gtag = function (): void {
                dataLayer.push(arguments as never);
            };
        }

        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        document.head.appendChild(gtmScript);

        const gtmNoScript = document.createElement('noscript');
        const gtmIframe = document.createElement('iframe');
        gtmIframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
        gtmIframe.height = '0';
        gtmIframe.width = '0';
        gtmIframe.style.display = 'none';
        gtmIframe.style.visibility = 'hidden';
        gtmNoScript.appendChild(gtmIframe);

        const bodyEl = document.querySelector('body');
        if (bodyEl) {
            bodyEl.insertAdjacentElement('afterbegin', gtmNoScript);
        }
    }

    toggleMenu() {
        this.showMenu = !this.showMenu;
        const currentHeight = this.heightService.getHeaderHeight();
        this.updateElementTop(currentHeight);
    }

    ngAfterViewInit() {
        this.postViewHeaderHeightSubscription = this.heightService.headerHeight$.subscribe(headerHeight => {
            this.updateElementTop(headerHeight);
        });

        window.addEventListener('resize', this.windowResizeHandler);
    }

    private updateElementTop(headerHeight: number) {
        const navbarOpen = document.querySelector('.navbar-collapse.show') !== null;
        const navbarAddOpen = document.querySelector('.navbar-collapse.add.show') !== null;

        let fixedOffset = 69;

        if (window.innerWidth <= 575) {
            if (navbarAddOpen) {
                fixedOffset = 90;
            } else {
                fixedOffset = navbarOpen ? 150 : 51;
            }
        }
        else if (window.innerWidth <= 767) {
            if (navbarAddOpen) {
                fixedOffset = 90;
            } else {
                fixedOffset = navbarOpen ? 150 : 57;
            }
        }
        else if (window.innerWidth <= 991) {
            if (navbarAddOpen) {
                fixedOffset = 90;
            } else {
                fixedOffset = navbarOpen ? 150 : 59;
            }
        }
        else if (window.innerWidth <= 1199) {
            if (navbarAddOpen) {
                fixedOffset = 110;
            } else {
                fixedOffset = navbarOpen ? 100 : 69;
            }
        }

        // This number used to be written as an inline `top` on `.commontopheight`,
        // pushing the content clear of the fixed header while the document itself
        // scrolled. The shell now positions its own scroll container, so the same
        // value is published as a custom property that `.app-scroll` and the
        // sidebar both read. See AppScrollService.
        //
        // The per-breakpoint constants run a few pixels taller than the navbar they
        // stand for (69 against a 65px bar on desktop). While the page scrolled under
        // the header that slack was invisible; with a fixed scroll container it shows
        // as a strip of page background under the navbar. With the menu closed the
        // header's measured bottom edge is used instead, so the container starts
        // exactly where the header ends. The open-menu offsets are left as they were.
        const menuOpen = navbarOpen || navbarAddOpen
            || document.querySelector('.navbar-collapse.collapsing') !== null;
        const header = document.querySelector<HTMLElement>('app-header-new .header.fixed-top');
        // The observer is created in ngOnInit, before the header renders, so the
        // header is attached here once it exists. Re-observing is a no-op.
        if (header) {
            this.resizeObserver?.observe(header);
        }
        const measuredBottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
        // No header at all (the sign-in pages hide it): nothing to clear.
        const offset = !header
            ? 0
            : !menuOpen && measuredBottom > 0 ? measuredBottom : headerHeight + fixedOffset;

        document.documentElement.style.setProperty('--app-header-offset', `${offset}px`);
        this.updateFooterHeight();
    }

    /**
     * Publishes the footer's measured height so the scroll container can stop
     * above it. Resolves to 0 on the routes that render no footer, which lets the
     * container run to the bottom of the viewport.
     */
    private updateFooterHeight() {
        const footer = document.querySelector('app-footer-new .base_footer') as HTMLElement | null;
        const footerHeight = footer ? footer.getBoundingClientRect().height : 0;
        document.documentElement.style.setProperty('--app-footer-height', `${footerHeight}px`);
    }

    private setupResizeObserver() {
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => {
                this.ngZone.run(() => {
                    const currentHeight = this.heightService.getHeaderHeight();
                    this.updateElementTop(currentHeight);
                });
            });

            // Watch the two bands that bound the scroll container. Both change height
            // responsively (the header also grows when the mobile navbar opens), and a
            // stale offset would leave the container overlapping one of them.
            // The header host never resizes (its content is position: fixed), so the
            // fixed box itself is watched too.
            const observed = [
                document.querySelector('app-header-new'),
                document.querySelector('app-header-new .header.fixed-top'),
                document.querySelector('app-footer-new')
            ];
            observed.forEach(element => {
                if (element) {
                    this.resizeObserver?.observe(element);
                }
            });
        }
    }

    private teardownResizeObserver() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    private initializeGlobalAuthListeners(): void {
        this.currentUserId = this.normalizeUserId(this.userState.user?._id);

        this.socketService.getUserDeletedEvent()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.handleUserDeletedEvent(event));

        // The RealWorld team changed this user's community: the page loads again in the right view
        this.communityMembership.showPendingNotice();
        this.socketService.getUserCommunityUpdatedEvent()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.communityMembership.communityChanged(event));

        this.userState.user$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user) => {
                this.currentUserId = this.normalizeUserId(user?._id);

                if (this.currentUserId) {
                    this.socketService.connect();
                    return;
                }

                this.socketService.disconnect();
            });

        if (typeof window === 'undefined') {
            return;
        }

        fromEvent<StorageEvent>(window, 'storage')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => event.key === this.appAuthService.logoutBroadcastStorageKey)
            )
            .subscribe((event) => this.handleLogoutBroadcast(event.newValue));

        this.watchConnectivity();
    }

    /**
     * Tell the user when the connection drops and when it comes back.
     *
     * Only the toast lives here. Refetching is left to the individual list pages via
     * NetworkStatusService.reconnected$, so a write, signing or payment flow can never
     * be replayed by a reconnect — a duplicated loan bid or fee payment is far worse
     * for a user than pressing refresh.
     */
    private watchConnectivity(): void {
        this.networkStatus.isOnline$
            .pipe(takeUntilDestroyed(this.destroyRef), skip(1))
            .subscribe((isOnline) => {
                if (isOnline) {
                    this.toastr.success('Back online.');
                } else {
                    this.toastr.warning('No internet connection. Some data may be out of date.');
                }
            });
    }

    private handleUserDeletedEvent(event: UserDeletedEventPayload): void {
        const deletedUserId = this.normalizeUserId(event?.userId);
        if (!deletedUserId || !this.currentUserId || deletedUserId !== this.currentUserId) {
            return;
        }

        this.appAuthService.logout({
            manual: false,
            showToast: false,
            redirectTo: '/sign-in',
            broadcast: true,
            broadcastReason: 'USER_DELETED',
            broadcastUserId: deletedUserId
        }).subscribe();
    }

    private handleLogoutBroadcast(rawValue: string | null): void {
        const logoutEvent = this.appAuthService.parseLogoutBroadcast(rawValue);
        if (!logoutEvent || !this.currentUserId) {
            return;
        }

        const broadcastUserId = this.normalizeUserId(logoutEvent.userId);
        if (broadcastUserId && broadcastUserId !== this.currentUserId) {
            return;
        }

        this.appAuthService.logout({
            manual: false,
            showToast: false,
            redirectTo: '/sign-in',
            broadcast: false,
            skipServerLogout: true
        }).subscribe();
    }

    private normalizeUserId(userId: unknown): string | null {
        if (userId === null || userId === undefined) {
            return null;
        }

        const normalizedUserId = String(userId).trim();
        return normalizedUserId.length ? normalizedUserId : null;
    }

    ngOnDestroy() {
        this.headerHeightSubscription?.unsubscribe();
        this.postViewHeaderHeightSubscription?.unsubscribe();
        this.gridViewSubscription?.unsubscribe();
        this.signInPageSubscription?.unsubscribe();
        this.teardownResizeObserver();
        if (this.darkModeMediaQuery && this.darkModeChangeHandler) {
            this.darkModeMediaQuery.removeEventListener('change', this.darkModeChangeHandler);
        }
        window.removeEventListener('resize', this.windowResizeHandler);
        window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    }
}
